import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import {
    getAllCredentials,
    getEnvironment,
    getCursor,
    getPageSize,
    getHasNextPage,
    getHasPrevPage,
    getLoading,
    getError,
    getCorrelationId,
    hasCredentials,
    getSearchQuery
} from '../../store/ClientCredentials/selectors';
import {
    setCursor,
    setSearchQuery
} from '../../store/ClientCredentials/reducer';
import { fetchCredentialsThunk, checkUserProfileThunk } from '../../store/ClientCredentials/thunks';
import { setApiEnvironment } from '../../api/apiClient';
import { selectUser } from '../../store/auth/selectors';
import { useAppDispatch } from '../../store/configureStore';

const MerchantManager: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    // Redux state
    const credentials = useSelector(getAllCredentials);
    const environment = useSelector(getEnvironment);
    const cursor = useSelector(getCursor);
    const pageSize = useSelector(getPageSize);
    const hasNextPage = useSelector(getHasNextPage);
    const hasPrevPage = useSelector(getHasPrevPage);
    const loading = useSelector(getLoading);
    const error = useSelector(getError);
    const correlationId = useSelector(getCorrelationId);
    const hasData = useSelector(hasCredentials);
    const user = useSelector(selectUser);
    const searchQuery = useSelector(getSearchQuery);

    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [localSearch, setLocalSearch] = useState(searchQuery || '');
    const [hasFetched, setHasFetched] = useState(false);
    const [hasAccess, setHasAccess] = useState(true);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Profile check — only on environment switch
    useEffect(() => {
        if (!user?.email) return;
        setHasFetched(false);
        setLocalSearch('');
        setApiEnvironment(environment);
        (dispatch as any)(checkUserProfileThunk()).then((access: boolean) => {
            setHasAccess(access);
            if (access) {
                (dispatch as any)(fetchCredentialsThunk()).finally(() => setHasFetched(true));
            } else {
                setHasFetched(true);
            }
        });
    }, [dispatch, environment, user?.email]);

    // Fetch merchants — on search/pagination changes (not profile check)
    useEffect(() => {
        if (!user?.email || !hasAccess) return;
        setApiEnvironment(environment);
        (dispatch as any)(fetchCredentialsThunk()).finally(() => setHasFetched(true));
    }, [cursor, pageSize, searchQuery]);

    // Handle next page
    const handleNextPage = () => {
        if (hasNextPage) {
            dispatch(setCursor(cursor + pageSize));
        }
    };

    // Handle previous page
    const handlePrevPage = () => {
        if (hasPrevPage) {
            dispatch(setCursor(Math.max(0, cursor - pageSize)));
        }
    };

    // Handle copy to clipboard
    const handleCopy = async (text: string, fieldId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldId);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalSearch(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            dispatch(setSearchQuery(value));
        }, 400);
    };

    return (
        <div className="merchant-manager">
            {/* Section heading */}
            <div className="merchant-manager-header">
                <h1 className="merchant-manager-title">
                    {environment === 'production'
                        ? t('merchants.storeMerchantId', { defaultValue: 'Store/Merchant ID' })
                        : t('merchants.testStoreMerchantId', { defaultValue: 'Test Store/Merchant ID' })}
                </h1>
                <hr className="section-title-divider" />
            </div>

            {/* Search - always show after first successful fetch */}
            {!error && hasFetched && hasAccess && (
                <div className="search-container">
                    <SearchIcon className="search-icon" />
                    <input
                        type="text"
                        className="search-input"
                        placeholder={t('merchants.searchIds', { defaultValue: 'Search IDs' })}
                        value={localSearch}
                        onChange={handleSearch}
                    />
                </div>
            )}

            <div className="credentials-card">
                {/* Loading state — show loader until first fetch completes */}
                {(!hasFetched || loading) && !hasData && (
                    <div className="loading-container">
                        <div className="loader-spinner"></div>
                        <p className="loading-text">{t('merchants.loadingCredentials')}</p>
                    </div>
                )}

                {/* Error state — only show after fetch completes and not loading */}
                {hasFetched && error && !loading && (
                    <div className="merchant-no-access">
                        <p className="merchant-no-access-text">
                            {error}
                        </p>
                        {correlationId && (
                            <p className="merchant-no-access-text">ID: {correlationId}</p>
                        )}
                    </div>
                )}

                {/* No data from search */}
                {hasFetched && !loading && credentials.length === 0 && !error && (
                    <div className="merchant-no-access">
                        <p className="merchant-no-access-text">
                            {t('merchants.noDataFound', { defaultValue: 'No data found' })}
                        </p>
                    </div>
                )}

                {/* Merchant Data */}
                {credentials.length > 0 && (
                    <>
                        <div className="merchant-data-container">
                            {credentials.map((item, index) => (
                                <div key={item.id || index} className="merchant-item">
                                    {/* Test Store ID */}
                                    <div className="field-container">
                                        <label className="field-label">
                                            {environment === 'production'
                                                ? t('merchants.storeId')
                                                : t('merchants.testStoreId')}
                                        </label>
                                        <div className="field-input-wrapper">
                                            <input
                                                type="text"
                                                value={item.storeId ?? ''}
                                                readOnly
                                                className="field-input"
                                            />
                                            {item.storeId && (
                                                <button
                                                    onClick={() => handleCopy(item.storeId, `store-${index}`)}
                                                    className={copiedField === `store-${index}` ? 'copy-button copied' : 'copy-button'}
                                                    title={t('merchants.copyToClipboard')}
                                                    aria-label={t('merchants.copyStoreId')}
                                                >
                                                    <ContentCopyIcon className="copy-icon" />
                                                </button>
                                            )}
                                            {copiedField === `store-${index}` && (
                                                <span className="copy-success-message">{t('applications.copied')}</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Test Merchant ID */}
                                    <div className="field-container">
                                        <label className="field-label">
                                            {environment === 'production'
                                                ? t('merchants.merchantIdMid')
                                                : t('merchants.testMerchantId')}
                                        </label>
                                        <div className="field-input-wrapper">
                                            <input
                                                type="text"
                                                value={item.merchantId}
                                                readOnly
                                                className="field-input"
                                            />
                                            {item.merchantId && (
                                                <button
                                                    onClick={() => handleCopy(item.merchantId, `merchant-${index}`)}
                                                    className={copiedField === `merchant-${index}` ? 'copy-button copied' : 'copy-button'}
                                                    title={t('merchants.copyToClipboard')}
                                                    aria-label={t('merchants.copyMerchantId')}
                                                >
                                                    <ContentCopyIcon className="copy-icon" />
                                                </button>
                                            )}
                                            {copiedField === `merchant-${index}` && (
                                                <span className="copy-success-message">{t('applications.copied')}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {(hasNextPage || hasPrevPage) && (
                            <div className="pagination-container">
                                {/* Previous Button */}
                                <button
                                    onClick={handlePrevPage}
                                    disabled={!hasPrevPage}
                                    className="pagination-button nav-button"
                                    aria-label={t('merchants.previousPage')}
                                >
                                    {t('common.previous')}
                                </button>

                                {/* Next Button */}
                                <button
                                    onClick={handleNextPage}
                                    disabled={!hasNextPage}
                                    className="pagination-button nav-button"
                                    aria-label={t('merchants.nextPage')}
                                >
                                    {t('common.next')}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default MerchantManager;
